/**
 * Lost Luggage — full feature inside one file to keep the surface small.
 *
 * Screens (router-state inside this component):
 *   home       → search airlines + recent saved claims + Start new claim CTA
 *   airline    → contact + portal + email + help for one airline
 *   wizard     → fills the traveler / flight / bag / photos sections
 *   summary    → generated claim text + Copy / Open Portal / Save / Email
 *   saved      → list of saved claims with status + edit
 *
 * Anonymous users get localStorage-only claim drafts. Signed-in users get
 * cloud-synced drafts via /api/lost-luggage/claims.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  Phone,
  EnvelopeSimple,
  Globe,
  Camera,
  ClipboardText,
  ShieldCheck,
  CheckCircle,
  FilePdf,
  Plus,
  Trash,
  X,
  CircleNotch,
  WarningCircle,
  Question,
  ArrowSquareOut,
  Image as ImageIcon,
  Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { getStoredSessionToken } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { getUserId } from "../../lib/user";
import { resizeToDataUrl } from "../../lib/imageUtils";
import { generateClaimPdf } from "../../lib/lostLuggagePdf";
import { openExternalUrl } from "../../lib/openExternalUrl";
import { isNative } from "../../lib/platform";
import { confirmDelete } from "../../lib/confirmDelete";
import ExternalPageModal from "./ExternalPageModal";
import BrandMark from "./BrandMark";

const LS_DRAFTS_KEY = "tiplingo_lost_luggage_drafts_v1";

function readDrafts() {
  try {
    const raw = localStorage.getItem(LS_DRAFTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function writeDrafts(drafts) {
  try {
    localStorage.setItem(LS_DRAFTS_KEY, JSON.stringify(drafts));
  } catch {
    /* non-fatal */
  }
}

const STATUS_LABELS = {
  draft: { label: "Draft", color: "#4A5D78", soft: "#EAF2FB" },
  submitted: { label: "Submitted", color: "#2A6FD8", soft: "#EAF2FB" },
  under_review: { label: "Under review", color: "#E55B2C", soft: "#FDE6D8" },
  found: { label: "Found", color: "#2E7D5A", soft: "#DCEFE3" },
  delivered: { label: "Delivered", color: "#2E7D5A", soft: "#DCEFE3" },
  closed: { label: "Closed", color: "#0E2C54", soft: "#EAF2FB" },
};

export default function LostLuggagePage({ onBack }) {
  const { user } = useAuth();
  const isSignedIn = !!user && !!getStoredSessionToken();
  const userId = getUserId();

  const [screen, setScreen] = useState("home"); // home|airline|wizard|summary|prep|saved
  const [prepFrom, setPrepFrom] = useState("summary"); // where prep should return to
  const [externalUrl, setExternalUrl] = useState(null); // when set, render in-app modal
  const [activeAirline, setActiveAirline] = useState(null);
  const [activeClaim, setActiveClaim] = useState(null);
  const [drafts, setDrafts] = useState(() => readDrafts());

  useEffect(() => writeDrafts(drafts), [drafts]);

  // Cloud sync for signed-in users (merge cloud onto local; cloud wins).
  useEffect(() => {
    if (!isSignedIn) return;
    api
      .get(`/lost-luggage/claims`, { params: { user_id: userId } })
      .then((r) => {
        const cloud = r.data?.items || [];
        if (!cloud.length) return;
        setDrafts((local) => {
          const ids = new Set(cloud.map((c) => c.id));
          const localOnly = local.filter((c) => !ids.has(c.id));
          return [...cloud, ...localOnly];
        });
      })
      .catch(() => {});
  }, [isSignedIn, userId]);

  function persistClaim(claim) {
    setDrafts((arr) => {
      const ix = arr.findIndex((c) => c.id === claim.id);
      if (ix === -1) return [claim, ...arr];
      const copy = [...arr];
      copy[ix] = claim;
      return copy;
    });
    // Best-effort cloud sync if signed in
    if (isSignedIn) {
      const exists = drafts.some((c) => c.id === claim.id);
      const url = exists
        ? `/lost-luggage/claims/${claim.id}`
        : `/lost-luggage/claims`;
      const fn = exists ? api.patch : api.post;
      fn(url, { ...claim, user_id: userId }).catch(() => {});
    }
  }

  function deleteClaim(id) {
    setDrafts((arr) => arr.filter((c) => c.id !== id));
    if (isSignedIn) {
      api
        .delete(`/lost-luggage/claims/${id}`, { params: { user_id: userId } })
        .catch(() => {});
    }
  }

  function startNewClaim(airline) {
    const claim = {
      id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      airline_id: airline?.id || null,
      airline_name: airline?.airline_name || "",
      traveler_name: "",
      traveler_email: "",
      preferred_language: "English",
      flight_number: "",
      booking_reference: "",
      travel_date: "",
      departure_airport: "",
      arrival_airport: "",
      connecting_airport: "",
      baggage_tag_number: "",
      checked_bag_count: 1,
      bag_type: "suitcase",
      brand: "",
      color: "",
      size: "",
      material: "",
      identifying_marks: "",
      contents_summary: "",
      estimated_value: "",
      incident_airport: "",
      time_discovered_missing: "",
      additional_notes: "",
      photos: [],
      status: "draft",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setActiveClaim(claim);
    setScreen("wizard");
  }

  /* ----- Screens ----- */

  if (screen === "home") {
    return (
      <Home
        onBack={onBack}
        drafts={drafts}
        onPickAirline={(a) => {
          setActiveAirline(a);
          setScreen("airline");
        }}
        onNewClaim={() => startNewClaim(null)}
        onOpenClaim={(c) => {
          setActiveClaim(c);
          setScreen("wizard");
        }}
        onSeeAll={() => setScreen("saved")}
      />
    );
  }
  if (screen === "airline") {
    return (
      <AirlineDetail
        airline={activeAirline}
        onBack={() => setScreen("home")}
        onPrepare={() => startNewClaim(activeAirline)}
        onOpenPortal={() => {
          // Open the prep screen with airline-only context (no claim yet)
          setActiveClaim({
            id: `tmp_${Date.now()}`,
            airline_id: activeAirline?.id || null,
            airline_name: activeAirline?.airline_name || "",
            traveler_name: "",
            traveler_email: "",
            photos: [],
            status: "draft",
          });
          setPrepFrom("airline");
          setScreen("prep");
        }}
      />
    );
  }
  if (screen === "wizard") {
    return (
      <ClaimWizard
        claim={activeClaim}
        onBack={() => setScreen("home")}
        onSave={(c) => {
          persistClaim(c);
          setActiveClaim(c);
          setScreen("summary");
        }}
      />
    );
  }
  if (screen === "summary") {
    return (
      <ClaimSummary
        claim={activeClaim}
        onBack={() => setScreen("wizard")}
        onSaveStatus={(status, ref) => {
          const next = {
            ...activeClaim,
            status,
            ...(ref ? { airline_claim_reference: ref } : {}),
            updated_at: new Date().toISOString(),
          };
          persistClaim(next);
          setActiveClaim(next);
          toast.success("Claim saved");
        }}
        onContinueToPortal={() => {
          setPrepFrom("summary");
          setScreen("prep");
        }}
        onHome={() => setScreen("home")}
      />
    );
  }
  if (screen === "prep") {
    return (
      <>
        <ClaimReviewBeforePortal
          claim={activeClaim}
          onBack={() => setScreen(prepFrom)}
          onOpenExternal={(url) => {
            if (isNative()) {
              // Native iOS → SFSafariViewController (has Done button)
              openExternalUrl(url);
            } else {
              // Web/preview → use our in-app modal so the user always has
              // a "Back to TipLingo" button on top, even when the airline
              // page is full-screen.
              setExternalUrl(url);
            }
          }}
        />
        {externalUrl && (
          <ExternalPageModal
            url={externalUrl}
            onClose={() => setExternalUrl(null)}
          />
        )}
      </>
    );
  }
  if (screen === "saved") {
    return (
      <SavedClaims
        drafts={drafts}
        onBack={() => setScreen("home")}
        onOpen={(c) => {
          setActiveClaim(c);
          setScreen("summary");
        }}
        onDelete={deleteClaim}
      />
    );
  }
  return null;
}

/* ---------- Home ---------- */

function Home({ onBack, drafts, onPickAirline, onNewClaim, onOpenClaim, onSeeAll }) {
  const [q, setQ] = useState("");
  const [airlines, setAirlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const debRef = useRef(null);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => {
      setLoading(true);
      api
        .get("/airlines", { params: q ? { q } : {} })
        .then((r) => setAirlines(r.data?.items || []))
        .catch(() => setAirlines([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(debRef.current);
  }, [q]);

  const recent = drafts.slice(0, 2);

  return (
    <div className="px-5 pt-4 pb-28" data-testid="lost-luggage-screen">
      <Header onBack={onBack} title="Lost Luggage Help" />

      <p className="text-navy-soft text-sm font-jakarta font-medium leading-snug mb-4 px-1">
        Missing bag? Find the right airline contact, organize your claim
        details, attach luggage photos, and open the official baggage claim
        portal fast.
      </p>

      {/* Search */}
      <div className="relative mb-3">
        <MagnifyingGlass
          size={18}
          weight="bold"
          color="#4A5D78"
          className="absolute left-3 top-1/2 -translate-y-1/2"
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search airline name"
          data-testid="ll-search"
          className="w-full bg-white border border-navy/10 rounded-2xl pl-10 pr-4 py-3 text-base font-jakarta text-navy outline-none shadow-soft focus:border-orange/40"
        />
      </div>

      {/* Start new claim CTA */}
      <button
        type="button"
        onClick={onNewClaim}
        data-testid="ll-new-claim-btn"
        className="w-full mb-4 bg-orange text-white font-jakarta font-extrabold text-base rounded-full py-3.5 shadow-soft flex items-center justify-center gap-2"
      >
        <Plus size={18} weight="bold" /> Start new claim
      </button>

      {recent.length > 0 && (
        <section className="mb-4">
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="font-jakarta font-extrabold text-navy text-base">
              My baggage claims
            </h2>
            <button
              type="button"
              onClick={onSeeAll}
              data-testid="ll-see-all-saved"
              className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider"
            >
              See all ({drafts.length})
            </button>
          </div>
          <div className="space-y-2">
            {recent.map((c) => (
              <SavedClaimCard key={c.id} claim={c} onClick={() => onOpenClaim(c)} />
            ))}
          </div>
        </section>
      )}

      <h2 className="font-jakarta font-extrabold text-navy text-base mb-2 px-1">
        Airlines
      </h2>
      {loading && (
        <div className="text-navy-soft text-sm py-6 text-center">
          <CircleNotch size={20} weight="bold" className="animate-spin inline" /> Loading airlines…
        </div>
      )}
      <div className="space-y-2.5" data-testid="ll-airline-list">
        {airlines.map((a) => (
          <AirlineCard key={a.id} airline={a} onClick={() => onPickAirline(a)} />
        ))}
        {!loading && airlines.length === 0 && (
          <div className="text-navy-soft text-sm py-6 text-center">
            No airlines match "{q}".
          </div>
        )}
      </div>

      {/* Missing-airline CTA — clickable mailto: link routes to TipLingo
          support so users can flag carriers we don't yet cover. */}
      <a
        href="mailto:support@tiplingo.com?subject=TipLingo%20%E2%80%94%20Missing%20airline%20in%20Lost%20Luggage&body=Hi%20TipLingo%20team%2C%0A%0AThe%20airline%20I%E2%80%99m%20looking%20for%20isn%E2%80%99t%20listed%20in%20Lost%20Luggage.%20Can%20you%20add%20it%3F%0A%0AAirline%20name%3A%20%0AFlight%20number%20(optional)%3A%20%0A%0AThanks!"
        data-testid="missing-airline-link"
        className="block mt-6 bg-white border border-navy/10 rounded-2xl px-4 py-4 shadow-soft active:bg-bone/60 transition-colors"
      >
        <div className="font-jakarta font-extrabold text-navy text-sm">
          Don't see your airline?
        </div>
        <div className="font-jakarta text-navy-soft text-xs mt-1 leading-relaxed">
          Let us know and we'll add it.{" "}
          <span className="text-orange font-bold underline">Email support →</span>
        </div>
      </a>

      {/* Affiliation disclaimer — required so Apple and users understand
          TipLingo is not endorsed by any airline. Styled as a visible info
          block so it can't be missed below the airline list. */}
      <div
        data-testid="airline-affiliation-disclaimer"
        className="mt-6 bg-bone border border-navy/10 rounded-2xl px-4 py-3 flex items-start gap-3"
      >
        <Info size={18} weight="duotone" color="#4A5D78" className="shrink-0 mt-0.5" />
        <p className="text-navy-soft text-xs font-jakarta font-medium leading-relaxed">
          Airline names and contact details are provided for traveler
          convenience. TipLingo is not affiliated with or endorsed by any
          airline. Please verify details with the airline's official website.
        </p>
      </div>

      <p className="text-navy-soft text-xs font-jakarta font-medium mt-3 leading-relaxed px-1">
        TipLingo prepares your claim and opens the airline's official portal.
        Final submission must be completed there. Airline policies and contact
        info may change — always verify with the airline directly.
      </p>
    </div>
  );
}

/* ---------- Components ---------- */

function Header({ onBack, title }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <button
        type="button"
        onClick={onBack}
        aria-label="Back"
        data-testid="ll-back"
        className="w-10 h-10 rounded-full bg-white shadow-soft border border-navy/10 flex items-center justify-center"
      >
        <CaretLeft size={20} weight="bold" color="#0E2C54" />
      </button>
      <h1 className="font-jakarta font-extrabold text-navy text-lg flex items-center gap-2">
        <img
          src="/assets/lost-luggage-icon.png"
          alt=""
          aria-hidden
          className="w-7 h-7 object-contain"
        />
        {title}
      </h1>
      <div className="w-10 h-10" />
    </div>
  );
}

function AirlineCard({ airline, onClick }) {
  const [logoOk, setLogoOk] = useState(!!airline.iata_code);
  const logoSrc = airline.iata_code
    ? `/assets/airlines/${airline.iata_code}.png`
    : null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`ll-airline-${airline.iata_code || airline.id}`}
      className="w-full bg-white rounded-2xl shadow-soft border border-navy/5 p-3 flex items-center gap-3 text-left active:bg-bone/30 transition-colors"
    >
      {logoOk && logoSrc ? (
        <img
          src={logoSrc}
          alt=""
          aria-hidden
          onError={() => setLogoOk(false)}
          className="shrink-0 w-11 h-11 rounded-xl object-contain bg-white border border-navy/5"
        />
      ) : (
        <div className="shrink-0 w-11 h-11 rounded-xl bg-forest-soft text-forest flex items-center justify-center font-jakarta font-extrabold text-base">
          {airline.iata_code || airline.airline_name.slice(0, 2).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-jakarta font-extrabold text-navy text-base leading-tight truncate">
          {airline.airline_name}
        </div>
        <div className="text-navy-soft text-xs font-jakarta font-medium mt-0.5 truncate">
          {airline.country}
          {airline.baggage_phone ? ` · ${airline.baggage_phone}` : ""}
        </div>
      </div>
      <CaretRight size={16} weight="bold" color="#4A5D78" />
    </button>
  );
}

function SavedClaimCard({ claim, onClick }) {
  const st = STATUS_LABELS[claim.status] || STATUS_LABELS.draft;
  const date = claim.travel_date || (claim.updated_at ? new Date(claim.updated_at).toLocaleDateString() : "—");
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="ll-saved-claim-card"
      className="w-full bg-white rounded-2xl shadow-soft border border-navy/5 p-3 flex items-center gap-3 text-left active:bg-bone/30 transition-colors"
    >
      <div className="shrink-0 w-11 h-11 rounded-xl bg-bone/60 border border-navy/5 flex items-center justify-center">
        <img
          src="/assets/lost-luggage-icon.png"
          alt=""
          aria-hidden
          className="w-8 h-8 object-contain"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-jakarta font-extrabold text-navy text-base leading-tight truncate">
          {claim.airline_name || "Airline TBD"} · {claim.flight_number || "—"}
        </div>
        <div className="text-navy-soft text-xs font-jakarta font-medium mt-0.5 truncate">
          {date}
          {claim.baggage_tag_number ? ` · Tag ${claim.baggage_tag_number}` : ""}
          {claim.airline_claim_reference ? ` · Ref ${claim.airline_claim_reference}` : ""}
        </div>
      </div>
      <span
        className="shrink-0 rounded-full text-[10px] font-jakarta font-extrabold uppercase tracking-wider px-2.5 py-1"
        style={{ backgroundColor: st.soft, color: st.color }}
      >
        {st.label}
      </span>
    </button>
  );
}

/* ---------- Airline Detail ---------- */

function AirlineDetail({ airline, onBack, onPrepare, onOpenPortal }) {
  if (!airline) return null;
  const needsCheck = airline.verified_status === "needs_verification";
  return (
    <div className="px-5 pt-4 pb-28" data-testid="ll-airline-detail">
      <Header onBack={onBack} title={airline.airline_name} />

      <div className="relative bg-white rounded-2xl shadow-soft border border-navy/5 p-5 mb-3 overflow-hidden">
        {airline.iata_code && (
          <img
            src={`/assets/airline-tails/${airline.iata_code}.png`}
            alt=""
            aria-hidden
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="pointer-events-none select-none absolute bottom-0 right-3 h-28 w-auto max-w-[42%] object-contain object-bottom"
            data-testid="ll-airline-tail"
          />
        )}
        <div className="flex items-start gap-3 relative">
          {airline.iata_code && (
            <img
              src={`/assets/airlines/${airline.iata_code}.png`}
              alt=""
              aria-hidden
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="shrink-0 w-14 h-14 rounded-xl object-contain bg-white border border-navy/10"
            />
          )}
          <div className="flex-1 min-w-0 pr-20">
            <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">
              {airline.country}
              {airline.iata_code ? ` · ${airline.iata_code}` : ""}
            </div>
            <h2 className="font-jakarta font-extrabold text-navy text-2xl leading-tight">
              {airline.airline_name}
            </h2>
          </div>
        </div>
        {airline.last_verified && (
          <div className="text-navy-soft text-xs font-jakarta font-medium mt-2">
            Last verified: {airline.last_verified}
          </div>
        )}
        {needsCheck && (
          <div className="mt-3 bg-orange-soft border border-orange/15 rounded-xl px-3 py-2 text-orange text-xs font-jakarta font-bold flex items-start gap-2">
            <WarningCircle size={16} weight="duotone" />
            Verify this contact info on the airline's official site before
            relying on it.
          </div>
        )}
      </div>

      <div className="space-y-2.5">
        {airline.baggage_phone && (
          <ActionRow
            href={`tel:${airline.baggage_phone.replace(/[^+0-9]/g, "")}`}
            Icon={Phone}
            label="Call baggage service"
            sub={airline.baggage_phone}
            testId="ll-call-btn"
            tone="navy"
          />
        )}
        {airline.lost_baggage_claim_url && (
          <button
            type="button"
            onClick={onOpenPortal}
            data-testid="ll-portal-btn"
            className="w-full block rounded-2xl shadow-soft border p-3.5 flex items-center gap-3 active:opacity-90 transition-opacity bg-orange text-white border-orange text-left"
          >
            <div className="shrink-0 w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
              <ArrowSquareOut size={18} weight="duotone" color="#fff" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-jakarta font-extrabold text-sm leading-tight">
                Open official claim portal
              </div>
              <div className="text-xs font-jakarta font-medium mt-0.5 truncate opacity-85">
                {hostnameOf(airline.lost_baggage_claim_url)}
              </div>
            </div>
            <CaretRight size={14} weight="bold" style={{ opacity: 0.7 }} />
          </button>
        )}
        {airline.baggage_email ? (
          <ActionRow
            href={`mailto:${airline.baggage_email}`}
            Icon={EnvelopeSimple}
            label="Email airline baggage team"
            sub={airline.baggage_email}
            testId="ll-email-btn"
          />
        ) : (
          <div className="bg-bone/60 rounded-2xl border border-navy/5 p-3 text-navy-soft text-xs font-jakarta font-medium">
            No direct public email available. Use official claim portal.
          </div>
        )}
        {airline.baggage_help_url && (
          <ActionRow
            href={airline.baggage_help_url}
            external
            Icon={Question}
            label="Baggage help center"
            sub={hostnameOf(airline.baggage_help_url)}
            testId="ll-help-btn"
          />
        )}
        {airline.notes && (
          <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-4">
            <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">
              Notes
            </div>
            <p className="text-navy text-sm font-jakarta font-medium leading-relaxed">
              {airline.notes}
            </p>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onPrepare}
        data-testid="ll-prepare-btn"
        className="w-full mt-5 bg-orange text-white font-jakarta font-extrabold text-base rounded-full py-3.5 shadow-soft flex items-center justify-center gap-2"
      >
        Prepare my claim
        <CaretRight size={16} weight="bold" />
      </button>
    </div>
  );
}

function ActionRow({ href, external, Icon, label, sub, testId, tone }) {
  const cls =
    tone === "orange"
      ? "bg-orange text-white border-orange"
      : tone === "navy"
      ? "bg-navy text-white border-navy"
      : "bg-white text-navy border-navy/10";
  const isHttp = typeof href === "string" && /^https?:\/\//i.test(href);
  const handleClick = (e) => {
    if (isHttp) {
      e.preventDefault();
      openExternalUrl(href);
    }
  };
  return (
    <a
      href={href}
      onClick={handleClick}
      target={external && !isHttp ? "_blank" : undefined}
      rel={external && !isHttp ? "noopener noreferrer" : undefined}
      data-testid={testId}
      className={`block rounded-2xl shadow-soft border p-3.5 flex items-center gap-3 active:opacity-90 transition-opacity ${cls}`}
    >
      <div className="shrink-0 w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
        <Icon size={18} weight="duotone" color={tone ? "#fff" : "#0E2C54"} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-jakarta font-extrabold text-sm leading-tight">
          {label}
        </div>
        {sub && (
          <div
            className="text-xs font-jakarta font-medium mt-0.5 truncate"
            style={{ opacity: tone ? 0.85 : 0.65 }}
          >
            {sub}
          </div>
        )}
      </div>
      <CaretRight size={14} weight="bold" style={{ opacity: 0.7 }} />
    </a>
  );
}

function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/* ---------- Claim Wizard ---------- */

const BAG_TYPES = ["suitcase", "duffel", "backpack", "box", "stroller", "sports equipment", "other"];

function ClaimWizard({ claim, onBack, onSave }) {
  const [draft, setDraft] = useState(claim);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const photoRef = useRef(null);

  function set(k, v) {
    setDraft((d) => ({ ...d, [k]: v, updated_at: new Date().toISOString() }));
  }

  async function onPhoto(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSavingPhoto(true);
    try {
      const dataUrl = await resizeToDataUrl(file, 1200, 0.78);
      setDraft((d) => ({
        ...d,
        photos: [...(d.photos || []), { label: "luggage", data_url: dataUrl }],
        updated_at: new Date().toISOString(),
      }));
    } catch (err) {
      toast.error(err.message || "Couldn't attach photo");
    } finally {
      setSavingPhoto(false);
    }
  }

  function removePhoto(i) {
    setDraft((d) => ({ ...d, photos: d.photos.filter((_, j) => j !== i) }));
  }

  function submit() {
    onSave(draft);
  }

  return (
    <div className="px-5 pt-4 pb-28" data-testid="ll-wizard">
      <Header onBack={onBack} title="Prepare claim" />

      {/* Traveler */}
      <Section title="Traveler information">
        <Field label="Full name" value={draft.traveler_name} onChange={(v) => set("traveler_name", v)} testId="ll-field-traveler_name" />
        <Field label="Phone" value={draft.traveler_phone} onChange={(v) => set("traveler_phone", v)} testId="ll-field-traveler_phone" inputMode="tel" />
        <Field label="Email" value={draft.traveler_email} onChange={(v) => set("traveler_email", v)} testId="ll-field-traveler_email" inputMode="email" />
        <Field label="Home address" value={draft.traveler_address} onChange={(v) => set("traveler_address", v)} testId="ll-field-traveler_address" />
        <Field label="Preferred language" value={draft.preferred_language} onChange={(v) => set("preferred_language", v)} testId="ll-field-preferred_language" />
      </Section>

      {/* Flight */}
      <Section title="Flight information">
        <Field label="Airline" value={draft.airline_name} onChange={(v) => set("airline_name", v)} testId="ll-field-airline_name" />
        <Field label="Flight number" placeholder="DL1234" value={draft.flight_number} onChange={(v) => set("flight_number", v)} testId="ll-field-flight_number" />
        <Field label="Booking / confirmation #" value={draft.booking_reference} onChange={(v) => set("booking_reference", v)} testId="ll-field-booking_reference" />
        <Field label="Ticket # (optional)" value={draft.ticket_number} onChange={(v) => set("ticket_number", v)} testId="ll-field-ticket_number" />
        <Field label="Date of travel" placeholder="Aug 14 2026" value={draft.travel_date} onChange={(v) => set("travel_date", v)} testId="ll-field-travel_date" />
        <Field label="Departure airport" placeholder="JFK" value={draft.departure_airport} onChange={(v) => set("departure_airport", v)} testId="ll-field-departure_airport" />
        <Field label="Arrival airport" placeholder="CDG" value={draft.arrival_airport} onChange={(v) => set("arrival_airport", v)} testId="ll-field-arrival_airport" />
        <Field label="Connecting airport (if any)" value={draft.connecting_airport} onChange={(v) => set("connecting_airport", v)} testId="ll-field-connecting_airport" />
        <Field label="Baggage tag #" placeholder="0060123456" value={draft.baggage_tag_number} onChange={(v) => set("baggage_tag_number", v)} testId="ll-field-baggage_tag_number" />
        <Field label="Number of checked bags" value={String(draft.checked_bag_count ?? "")} onChange={(v) => set("checked_bag_count", parseInt(v, 10) || 0)} testId="ll-field-checked_bag_count" inputMode="numeric" />
        <Field label="Where bag was reported missing" value={draft.incident_airport} onChange={(v) => set("incident_airport", v)} testId="ll-field-incident_airport" />
        <Field label="Time discovered missing" value={draft.time_discovered_missing} onChange={(v) => set("time_discovered_missing", v)} testId="ll-field-time_discovered_missing" />
      </Section>

      {/* Bag */}
      <Section title="Luggage description">
        <label className="block">
          <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">Bag type</div>
          <select
            value={draft.bag_type}
            onChange={(e) => set("bag_type", e.target.value)}
            data-testid="ll-field-bag_type"
            className="w-full bg-bone/70 border border-navy/10 rounded-xl px-3 py-2.5 font-jakarta font-semibold text-navy outline-none focus:border-orange/40"
          >
            {BAG_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <Field label="Brand" value={draft.brand} onChange={(v) => set("brand", v)} testId="ll-field-brand" />
        <Field label="Color" value={draft.color} onChange={(v) => set("color", v)} testId="ll-field-color" />
        <Field label="Size" placeholder="Carry-on / Medium / Large" value={draft.size} onChange={(v) => set("size", v)} testId="ll-field-size" />
        <Field label="Material" value={draft.material} onChange={(v) => set("material", v)} testId="ll-field-material" />
        <Field label="Identifying marks" placeholder="Red bow on handle, sticker on lid…" value={draft.identifying_marks} onChange={(v) => set("identifying_marks", v)} testId="ll-field-identifying_marks" />
        <TextArea label="Contents summary" value={draft.contents_summary} onChange={(v) => set("contents_summary", v)} testId="ll-field-contents_summary" />
        <Field label="Estimated value" placeholder="$ 850" value={draft.estimated_value} onChange={(v) => set("estimated_value", v)} testId="ll-field-estimated_value" />
        <TextArea label="Additional notes" value={draft.additional_notes} onChange={(v) => set("additional_notes", v)} testId="ll-field-additional_notes" />
        <div className="bg-orange-soft border border-orange/15 rounded-xl p-3 text-orange text-xs font-jakarta font-bold flex items-start gap-2">
          <WarningCircle size={16} weight="duotone" className="shrink-0 mt-0.5" />
          Do not include passwords, financial details, or private medical
          information in the contents description.
        </div>
      </Section>

      {/* Photos */}
      <Section title="Luggage photos">
        {draft.photos?.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-1" data-testid="ll-photo-grid">
            {draft.photos.map((p, i) => (
              <div key={i} className="relative">
                <img src={p.data_url} alt="" className="w-full h-24 object-cover rounded-xl border border-navy/10" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  aria-label="Remove photo"
                  data-testid={`ll-photo-remove-${i}`}
                  className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full shadow-soft flex items-center justify-center"
                >
                  <X size={12} weight="bold" color="#0E2C54" />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => photoRef.current?.click()}
          data-testid="ll-add-photo-btn"
          disabled={savingPhoto}
          className="w-full h-24 rounded-xl border-2 border-dashed border-navy/20 bg-bone/40 flex flex-col items-center justify-center gap-1.5 text-navy-soft font-jakarta font-medium disabled:opacity-60"
        >
          {savingPhoto ? (
            <CircleNotch size={20} weight="bold" className="animate-spin" />
          ) : (
            <>
              <Camera size={22} weight="duotone" color="#0E2C54" />
              <span className="text-sm">Add luggage photo</span>
            </>
          )}
        </button>
        <input
          ref={photoRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onPhoto}
          data-testid="ll-photo-input"
        />
      </Section>

      <button
        type="button"
        onClick={submit}
        data-testid="ll-wizard-save-btn"
        className="w-full mt-2 bg-orange text-white font-jakarta font-extrabold text-base rounded-full py-3.5 shadow-soft"
      >
        Generate claim summary
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-5">
      <h2 className="font-jakarta font-extrabold text-navy text-base mb-2 px-1">{title}</h2>
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-4 space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, testId, inputMode = "text" }) {
  return (
    <label className="block">
      <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">{label}</div>
      <input
        inputMode={inputMode}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-bone/70 border border-navy/10 rounded-xl px-3 py-2.5 text-base font-jakarta font-medium text-navy outline-none focus:border-orange/40"
      />
    </label>
  );
}
function TextArea({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">{label}</div>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        rows={3}
        className="w-full bg-bone/70 border border-navy/10 rounded-xl px-3 py-2.5 text-base font-jakarta text-navy outline-none focus:border-orange/40 resize-none"
      />
    </label>
  );
}

/* ---------- Summary ---------- */

function ClaimSummary({ claim, onBack, onSaveStatus, onContinueToPortal, onHome }) {
  const [refNum, setRefNum] = useState(claim.airline_claim_reference || "");
  const [status, setStatus] = useState(claim.status || "draft");
  const [airline, setAirline] = useState(null);

  useEffect(() => {
    if (!claim.airline_id) return;
    api
      .get(`/airlines/${claim.airline_id}`)
      .then((r) => setAirline(r.data))
      .catch(() => {});
  }, [claim.airline_id]);

  const summaryText = useMemo(() => buildSummary(claim), [claim]);

  async function copySummary() {
    try {
      await navigator.clipboard.writeText(summaryText);
      toast.success("Claim summary copied");
    } catch {
      toast.error("Couldn't copy — please try again");
    }
  }

  function openPortal() {
    const url = airline?.lost_baggage_claim_url;
    if (!url) {
      toast.error("No portal link saved for this airline");
      return;
    }
    onContinueToPortal?.();
  }

  const [pdfBusy, setPdfBusy] = useState(false);
  async function exportPdf(withPhotos) {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      await generateClaimPdf(claim, { withPhotos });
      toast.success(
        withPhotos ? "PDF with photos downloaded" : "Text-only PDF downloaded"
      );
    } catch (err) {
      toast.error(err?.message || "Couldn't generate PDF");
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div className="px-5 pt-4 pb-28" data-testid="ll-summary">
      <Header onBack={onBack} title="Claim summary" />

      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-4 mb-3">
        <pre
          className="text-navy text-sm font-jakarta whitespace-pre-wrap leading-relaxed"
          data-testid="ll-summary-text"
        >
{summaryText}
        </pre>
      </div>

      {claim.photos?.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {claim.photos.map((p, i) => (
            <img
              key={i}
              src={p.data_url}
              alt={`luggage ${i + 1}`}
              className="w-full h-24 object-cover rounded-xl border border-navy/10"
            />
          ))}
        </div>
      )}

      <div className="space-y-2.5">
        <button
          type="button"
          onClick={copySummary}
          data-testid="ll-copy-btn"
          className="w-full bg-navy text-white rounded-full py-3 font-jakarta font-extrabold flex items-center justify-center gap-2"
        >
          <ClipboardText size={18} weight="bold" /> Copy claim summary
        </button>
        <button
          type="button"
          onClick={openPortal}
          data-testid="ll-open-portal-btn"
          disabled={!airline?.lost_baggage_claim_url}
          className="w-full bg-orange text-white rounded-full py-3 font-jakarta font-extrabold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <ArrowSquareOut size={18} weight="bold" /> File lost luggage claim
        </button>
        <button
          type="button"
          onClick={() => exportPdf(true)}
          data-testid="ll-export-pdf-photos-btn"
          disabled={pdfBusy}
          className="w-full bg-forest text-white rounded-full py-3 font-jakarta font-extrabold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {pdfBusy ? (
            <CircleNotch size={18} weight="bold" className="animate-spin" />
          ) : (
            <FilePdf size={18} weight="bold" />
          )}
          Download PDF (with photos)
        </button>
        <button
          type="button"
          onClick={() => exportPdf(false)}
          data-testid="ll-export-pdf-text-btn"
          disabled={pdfBusy}
          className="w-full bg-white border border-navy/15 text-navy rounded-full py-3 font-jakarta font-extrabold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <FilePdf size={18} weight="duotone" color="#0E2C54" /> Text-only PDF (smaller)
        </button>
      </div>

      {/* Status tracker */}
      <Section title="Status & airline reference">
        <Field
          label="Airline claim reference #"
          value={refNum}
          onChange={setRefNum}
          placeholder="JFKDL12345"
          testId="ll-claim-ref"
        />
        <label className="block">
          <div className="text-navy-soft text-xs font-jakarta font-bold uppercase tracking-wider mb-1">
            Status
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="ll-claim-status"
            className="w-full bg-bone/70 border border-navy/10 rounded-xl px-3 py-2.5 font-jakarta font-semibold text-navy outline-none focus:border-orange/40"
          >
            {Object.keys(STATUS_LABELS).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s].label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => onSaveStatus(status, refNum)}
          data-testid="ll-save-claim-btn"
          className="w-full mt-1 bg-white border border-navy/10 rounded-full py-3 font-jakarta font-bold text-navy flex items-center justify-center gap-2"
        >
          <CheckCircle size={18} weight="duotone" color="#2E7D5A" /> Save claim
        </button>
      </Section>

      <div className="bg-orange-soft border border-orange/15 rounded-2xl p-4 flex items-start gap-3 mb-3">
        <ShieldCheck size={22} weight="duotone" color="#E55B2C" className="shrink-0 mt-0.5" />
        <p className="text-navy text-xs font-jakarta font-medium leading-relaxed">
          TipLingo helps prepare your claim. Final submission must be
          completed through the airline's official baggage claim portal.
          TipLingo does not guarantee claim approval, baggage recovery, or
          airline response time.
        </p>
      </div>

      <button
        type="button"
        onClick={onHome}
        data-testid="ll-summary-home-btn"
        className="w-full bg-white border border-navy/10 rounded-full py-3 font-jakarta font-bold text-navy"
      >
        Back to Lost Luggage Home
      </button>
    </div>
  );
}

function buildSummary(c) {
  const L = (label, v) => (v ? `${label}: ${v}\n` : "");
  let s = "Lost/Delayed Baggage Claim Information\n\n";
  s += "Traveler Information\n";
  s += L("Name", c.traveler_name);
  s += L("Phone", c.traveler_phone);
  s += L("Email", c.traveler_email);
  s += L("Address", c.traveler_address);
  s += L("Preferred Language", c.preferred_language);
  s += "\nFlight Information\n";
  s += L("Airline", c.airline_name);
  s += L("Flight Number", c.flight_number);
  s += L("Date of Travel", c.travel_date);
  s += L("Departure Airport", c.departure_airport);
  s += L("Arrival Airport", c.arrival_airport);
  s += L("Connecting Airport", c.connecting_airport);
  s += L("Booking Reference", c.booking_reference);
  s += L("Ticket Number", c.ticket_number);
  s += L("Baggage Tag Number", c.baggage_tag_number);
  s += L("Number of Checked Bags", c.checked_bag_count);
  s += "\nLuggage Description\n";
  s += L("Bag Type", c.bag_type);
  s += L("Brand", c.brand);
  s += L("Color", c.color);
  s += L("Size", c.size);
  s += L("Material", c.material);
  s += L("Identifying Marks", c.identifying_marks);
  s += L("Contents Summary", c.contents_summary);
  s += L("Estimated Value", c.estimated_value);
  s += "\nIncident Details\n";
  s += L("Airport Where Bag Was Reported Missing", c.incident_airport);
  s += L("Time Discovered Missing", c.time_discovered_missing);
  s += L("Additional Notes", c.additional_notes);
  if (c.photos?.length) {
    s += `\nAttached Photos: ${c.photos.length} photo(s)\n`;
  }
  if (c.airline_claim_reference) {
    s += `\nAirline Claim Reference: ${c.airline_claim_reference}\n`;
  }
  return s.trim();
}

/* ---------- Saved Claims list ---------- */

function SavedClaims({ drafts, onBack, onOpen, onDelete }) {
  return (
    <div className="px-5 pt-4 pb-28" data-testid="ll-saved-claims">
      <Header onBack={onBack} title="My baggage claims" />
      {drafts.length === 0 && (
        <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-8 text-center">
          <img
            src="/assets/lost-luggage-icon.png"
            alt=""
            aria-hidden
            className="w-12 h-12 object-contain mx-auto mb-2"
          />
          <div className="font-jakarta font-extrabold text-navy text-lg mb-1">
            No claims yet
          </div>
          <div className="text-navy-soft text-sm font-jakarta font-medium">
            Start a new claim from the Lost Luggage home.
          </div>
        </div>
      )}
      <div className="space-y-2">
        {drafts.map((c) => (
          <div
            key={c.id}
            className="bg-white rounded-2xl shadow-soft border border-navy/5 p-3 flex items-center gap-3"
          >
            <button
              type="button"
              onClick={() => onOpen(c)}
              className="flex-1 min-w-0 flex items-center gap-3 text-left"
              data-testid={`ll-open-claim-${c.id}`}
            >
              <div className="shrink-0 w-11 h-11 rounded-xl bg-bone/60 border border-navy/5 flex items-center justify-center">
                <img
                  src="/assets/lost-luggage-icon.png"
                  alt=""
                  aria-hidden
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-jakarta font-extrabold text-navy text-base leading-tight truncate">
                  {c.airline_name || "Airline TBD"} · {c.flight_number || "—"}
                </div>
                <div className="text-navy-soft text-xs font-jakarta font-medium mt-0.5 truncate">
                  {(STATUS_LABELS[c.status] || STATUS_LABELS.draft).label}
                  {c.airline_claim_reference ? ` · Ref ${c.airline_claim_reference}` : ""}
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={async () => {
                if (await confirmDelete()) onDelete(c.id);
              }}
              aria-label="Delete claim"
              data-testid={`ll-delete-claim-${c.id}`}
              className="shrink-0 w-9 h-9 rounded-full hover:bg-bone/70 flex items-center justify-center"
            >
              <Trash size={18} weight="duotone" color="#9CA3AF" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ---------- Pre-portal review (TipLingo owns the recap, then hands off) ---------- */

function ClaimReviewBeforePortal({ claim, onBack, onOpenExternal }) {
  const [airline, setAirline] = useState(null);

  useEffect(() => {
    if (!claim?.airline_id) return;
    api
      .get(`/airlines/${claim.airline_id}`)
      .then((r) => setAirline(r.data))
      .catch(() => {});
  }, [claim?.airline_id]);

  const summaryText = useMemo(() => buildSummary(claim), [claim]);

  async function copySummary() {
    try {
      await navigator.clipboard.writeText(summaryText);
      toast.success("Claim summary copied — paste into the airline form");
    } catch {
      toast.error("Couldn't copy — please try again");
    }
  }

  function handleContinue() {
    const url = airline?.lost_baggage_claim_url;
    if (!url) {
      toast.error("No portal link saved for this airline");
      return;
    }
    onOpenExternal?.(url);
  }

  const hostname = airline?.lost_baggage_claim_url
    ? hostnameOf(airline.lost_baggage_claim_url)
    : "";

  // Decide which sections actually have content to show.
  const hasTraveler =
    claim.traveler_name ||
    claim.traveler_phone ||
    claim.traveler_email ||
    claim.traveler_address;
  const hasFlight =
    claim.airline_name ||
    claim.flight_number ||
    claim.travel_date ||
    claim.departure_airport ||
    claim.arrival_airport ||
    claim.baggage_tag_number ||
    claim.booking_reference;
  const hasBag = claim.bag_type || claim.color || claim.brand;
  const hasPhotos = claim.photos?.length > 0;

  return (
    <div className="px-5 pt-4 pb-28" data-testid="ll-prep-screen">
      <Header onBack={onBack} title="Review before submitting" />

      <div className="bg-orange-soft border border-orange/20 rounded-2xl p-4 mb-3" data-testid="ll-prep-banner">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-xl bg-orange text-white flex items-center justify-center">
            <ArrowSquareOut size={18} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-orange font-jakarta font-extrabold text-sm mb-0.5">
              You're about to leave TipLingo
            </div>
            <div className="text-navy text-sm font-jakarta leading-snug">
              We'll open the official airline claim portal in a secure in-app
              browser. Tap <b>Done</b> (iOS) or your browser's back button to
              return here when you're finished.
            </div>
          </div>
        </div>
      </div>

      {hasTraveler && (
        <Section title="Traveler">
          <div className="bg-white border border-navy/10 rounded-2xl p-3 space-y-1.5">
            {claim.traveler_name && (
              <RecapRow label="Name" value={claim.traveler_name} />
            )}
            {claim.traveler_phone && (
              <RecapRow label="Phone" value={claim.traveler_phone} />
            )}
            {claim.traveler_email && (
              <RecapRow label="Email" value={claim.traveler_email} />
            )}
            {claim.traveler_address && (
              <RecapRow label="Address" value={claim.traveler_address} />
            )}
          </div>
        </Section>
      )}

      {hasFlight && (
        <Section title="Flight">
          <div className="bg-white border border-navy/10 rounded-2xl p-3 space-y-1.5">
            {claim.airline_name && (
              <RecapRow label="Airline" value={claim.airline_name} />
            )}
            {claim.flight_number && (
              <RecapRow label="Flight" value={claim.flight_number} />
            )}
            {claim.travel_date && (
              <RecapRow label="Date" value={claim.travel_date} />
            )}
            {(claim.departure_airport || claim.arrival_airport) && (
              <RecapRow
                label="Route"
                value={`${claim.departure_airport || "—"} → ${
                  claim.arrival_airport || "—"
                }`}
              />
            )}
            {claim.baggage_tag_number && (
              <RecapRow label="Bag tag #" value={claim.baggage_tag_number} />
            )}
            {claim.booking_reference && (
              <RecapRow label="Booking ref" value={claim.booking_reference} />
            )}
          </div>
        </Section>
      )}

      {hasBag && (
        <Section title="Bag description">
          <div className="bg-white border border-navy/10 rounded-2xl p-3 space-y-1.5">
            {claim.bag_type && (
              <RecapRow label="Type" value={claim.bag_type} />
            )}
            {claim.brand && <RecapRow label="Brand" value={claim.brand} />}
            {claim.color && <RecapRow label="Color" value={claim.color} />}
            {claim.size && <RecapRow label="Size" value={claim.size} />}
            {claim.identifying_marks && (
              <RecapRow label="Marks" value={claim.identifying_marks} />
            )}
          </div>
        </Section>
      )}

      {hasPhotos && (
        <Section title={`Photos (${claim.photos.length})`}>
          <div className="grid grid-cols-3 gap-2">
            {claim.photos.map((p, i) => (
              <img
                key={i}
                src={p.data_url}
                alt={`luggage ${i + 1}`}
                className="w-full h-20 object-cover rounded-xl border border-navy/10"
              />
            ))}
          </div>
        </Section>
      )}

      {!hasTraveler && !hasFlight && !hasBag && !hasPhotos && (
        <div className="bg-bone/40 border border-navy/10 rounded-2xl p-4 mb-3 text-center" data-testid="ll-prep-noclaim">
          <div className="font-jakarta font-extrabold text-navy text-base mb-1">
            No claim built yet
          </div>
          <p className="text-navy-soft text-sm font-jakarta">
            You're heading straight to the airline portal. To save your info
            in TipLingo (with photos and a PDF you can keep), tap{" "}
            <b>Back</b> and choose <b>Prepare my claim</b> first.
          </p>
        </div>
      )}

      <Section title="Airline contact">
        <div className="bg-white border border-navy/10 rounded-2xl p-3 space-y-1.5">
          {airline?.airline_name && (
            <RecapRow label="Airline" value={airline.airline_name} />
          )}
          {airline?.baggage_phone && (
            <RecapRow label="Phone" value={airline.baggage_phone} />
          )}
          {hostname && <RecapRow label="Portal" value={hostname} />}
        </div>
      </Section>

      <div className="sticky bottom-20 mt-4 space-y-2.5 bg-bone/95 backdrop-blur-sm pt-2 -mx-5 px-5">
        <button
          type="button"
          onClick={copySummary}
          data-testid="ll-prep-copy-btn"
          className="w-full bg-navy text-white rounded-full py-3 font-jakarta font-extrabold flex items-center justify-center gap-2"
        >
          <ClipboardText size={18} weight="bold" /> Copy summary to clipboard
        </button>
        <button
          type="button"
          onClick={handleContinue}
          data-testid="ll-prep-continue-btn"
          disabled={!airline?.lost_baggage_claim_url}
          className="w-full bg-orange text-white rounded-full py-3.5 font-jakarta font-extrabold flex items-center justify-center gap-2 disabled:opacity-60 shadow-soft"
        >
          Continue to airline claim portal{" "}
          <ArrowSquareOut size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function RecapRow({ label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <div className="shrink-0 w-20 text-navy-soft font-jakarta font-bold uppercase text-[10px] tracking-wider pt-0.5">
        {label}
      </div>
      <div className="flex-1 text-navy font-jakarta font-semibold leading-snug break-words">
        {value}
      </div>
    </div>
  );
}
